import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Flex,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Input,
  InputGroup,
  InputLeftElement,
  useDisclosure,
  useToast,
  FormControl,
  FormLabel
} from '@chakra-ui/react'
import { FaList, FaPlus, FaSearch, FaTrash, FaEdit } from 'react-icons/fa'
import { getAllArticleRules } from '../api/getAllArticleRules'
import { LABEL, MESSAGE } from '../../../constants'
import {
  getArticles,
  getExpenseGroups,
  getOrganisations
} from '../../MainPage/api/getNomenclatures'
import ExpenseGroupsDropDown from '../../MainPage/components/DocumentProcessPage/ExpenseGroupDropDown'
import { addArticleRules } from '../api/addArticleRules'
import DropdownMultiSelect from './DropdownMultiSelect'
import ArticleDropDown from '../../MainPage/components/DocumentProcessPage/ArticleDropDown'
import { getCompanies } from '../../SettingsPage/api'
import { deleteArticleRule } from '../api/deleteArticleRule'
import ListModal from './ListModal'
import FilterPopoverWithCheck from '../../MainPage/components/DocumentTable/FilterPopoverWithCheck'
import FilterMultiSelectPopover from '../../MainPage/components/DocumentTable/FilterMultiSelectPopover'

const ArticlesRulesTable = () => {
  const [data, setData] = useState({ content: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [pagination, setPagination] = useState({ pageNumber: 0, size: 20 })
  const [totalPages, setTotalPages] = useState(1)
  const [companies, setCompanies] = useState([])
  const [loadingCompany, setLoadingCompany] = useState(true)
  const [allExpenseGroups, setAllExpenseGroups] = useState([])
  const [loadingExpenseGroups, setLoadingExpenseGroups] = useState(true)
  const [organisations, setOrganisations] = useState([])
  const [companyFilter, setCompanyFilter] = useState([])
  const [expenseGroups, setExpenseGroups] = useState([])
  const toast = useToast()

  const {
    isOpen: isListOpen,
    onOpen: onListOpen,
    onClose: onListClose
  } = useDisclosure()
  const [selectedArticles, setSelectedArticles] = useState([])
  const [selectedGroupName, setSelectedGroupName] = useState('')

  const {
    isOpen: isUpsertOpen,
    onOpen: onUpsertOpen,
    onClose: onUpsertClose
  } = useDisclosure()

  const [modalData, setModalData] = useState({
    id: null,
    companyCif: '',
    expenseGroupId: '',
    articleIds: []
  })

  const fetchOrganisations = async () => {
      try {
          const response = await getOrganisations();
          setOrganisations(response || []);
      } catch (error) {
          console.error(MESSAGE.ORGANISATION_ERROR.console, error);
          toast({
              title: MESSAGE.ERROR.label,
              description: MESSAGE.ORGANISATION_ERROR.label,
              status: "error",
              duration: 5000,
              isClosable: true,
          });
      }
  };

  const fetchRules = async () => {
    setLoading(true)
    try {
      const { pageNumber, size } = pagination
      const response = await getAllArticleRules({
        expenseGroups: expenseGroups.map((item) => item.name),
        cifs: companyFilter,
        searchTerm: searchTerm || null,
        pageNumber,
        size,
        orderByList: null
      })
      setData(response)
      setTotalPages(
        Math.ceil((response.total || response.content.length) / size)
      )
    } catch (error) {
      toast({
        title: 'Eroare',
        description: MESSAGE.RULES.errorFetch,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrganisations()
  }, [])

  useEffect(() => {
    getCompanies()
      .then((response) => {
        setCompanies(response)
        setLoadingCompany(false)
      })
      .catch((error) => {
        console.error('Eroare la preluarea companiilor:', error)
        setLoadingCompany(false)
      })
  }, [])

  useEffect(() => {
    getExpenseGroups({
      orderByList: [{ field: 'name', direction: 'ASCENDING' }]
    })
      .then((response) => {
        setAllExpenseGroups(response)
        setLoadingExpenseGroups(false)
      })
      .catch((error) => {
        console.error('Eroare la preluarea grupurilor de cheltuieli:', error)
        setLoadingExpenseGroups(false)
      })
  }, [])

  useEffect(() => {
    fetchRules()
  }, [searchTerm, pagination, companyFilter, expenseGroups])

  const openList = (group) => {
    setSelectedArticles(group.articles || [])
    const [, expenseCode] = group.worldClassExpenseGroup.split('_')
    const expense = allExpenseGroups.find(
      (group) => group.name === expenseCode || group.id === expenseCode
    )
    setSelectedGroupName(expense?.details || group.id)
    onListOpen()
  }

  const openCreate = () => {
    setModalData({
      id: null,
      companyCif: '',
      expenseGroupId: '',
      articleIds: []
    })
    onUpsertOpen()
  }

  const openEdit = (group) => {
    const [companyPart] = group.worldClassExpenseGroup.split('_')
    setModalData({
      id: group.id,
      companyCif: companyPart,
      expenseGroupId: group.worldClassExpenseGroup,
      articleIds: group.articles ? group.articles.map((a) => a.id) : []
    })
    onUpsertOpen()
  }

  const handleUpsertSubmit = async () => {
    const { id, expenseGroupId, articleIds } = modalData

    const isNew = id === null

    try {
      if (isNew) {
        await addArticleRules(expenseGroupId, articleIds)
        toast({
          title: 'Succes',
          description: MESSAGE.RULES.addRuleSuccess,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      } else {
        await addArticleRules(expenseGroupId, articleIds)
        toast({
          title: 'Succes',
          description: MESSAGE.RULES.updateRuleSuccess,
          status: 'success',
          duration: 5000,
          isClosable: true
        })
      }

      onUpsertClose()
      setModalData({ id: null, companyCif: '', expenseGroupId: '', articleIds: [] })
      fetchRules()
    } catch (error) {
      toast({
        title: 'Eroare',
        description:
          error.response?.data?.message ||
          (isNew
            ? MESSAGE.RULES.addRuleError
            : MESSAGE.RULES.updateRuleError),
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const handleDelete = async (id) => {
    try {
      await deleteArticleRule(id)
      toast({
        title: 'Success',
        description: MESSAGE.RULES.deleteRuleSuccess,
        status: 'success',
        duration: 5000,
        isClosable: true
      })
      fetchRules()
    } catch (error) {
      toast({
        title: 'Error',
        description: MESSAGE.RULES.deleteRuleError,
        status: 'error',
        duration: 5000,
        isClosable: true
      })
    }
  }

  const renderUpsertModal = () => {
    if (!isUpsertOpen) return null

    const { id, companyCif, expenseGroupId, articleIds } = modalData
    const isNew = id === null

    return (
      <Modal isOpen={isUpsertOpen} onClose={() => {
        onUpsertClose()
        setModalData({ id: null, companyCif: '', expenseGroupId: '', articleIds: [] })
      }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isNew
              ? LABEL.ARTICLE_RULES_TABLE.createModalTitle || 'Adaugă Regulă'
              : LABEL.ARTICLE_RULES_TABLE.editRuleTitle || 'Editare Regulă'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb="3">
              <FormLabel textColor="gray.500" fontSize="sm" mb="1">
                {LABEL.ARTICLE_RULES_TABLE.company}
              </FormLabel>
              <ArticleDropDown
                value={companyCif}
                onChange={(newCif) =>
                  setModalData((prev) => ({ ...prev, companyCif: newCif }))
                }
                articles={companies}
                loading={loadingCompany}
              />
            </FormControl>

            <FormControl mb="3">
              <FormLabel textColor="gray.500" fontSize="sm" mb="1">
                {LABEL.ARTICLE_RULES_TABLE.expenseGroup}
              </FormLabel>
              <ExpenseGroupsDropDown
                value={expenseGroupId}
                loading={loadingExpenseGroups}
                expenseGroups={allExpenseGroups.filter((group) =>
                  group.id.startsWith(`${companyCif}_`)
                )}
                onChange={(newGrp) =>
                  setModalData((prev) => ({ ...prev, expenseGroupId: newGrp }))
                }
                id={true}
              />
            </FormControl>

            <FormControl>
              <FormLabel textColor="gray.500" fontSize="sm" mb="1">
                {LABEL.ARTICLE_RULES_TABLE.articleNumber}
              </FormLabel>
              <DropdownMultiSelect
                fetchItems={getArticles}
                initialSelected={articleIds}
                displayField="name"
                extractValue={(item) => item.id}
                onChange={(newIds) =>
                  setModalData((prev) => ({ ...prev, articleIds: newIds }))
                }
                cif={companyCif}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="blue" onClick={handleUpsertSubmit}>
              {isNew
                ? LABEL.RULES_TABLE.addRuleButton
                : LABEL.ARTICLE_RULES_TABLE.saveChangesButton || 'Salvează Modificările'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    )
  }

  const renderTable = () => {
    if (loading)
      return (
        <Box textAlign="center" p="4">
          <Spinner size="lg" />
        </Box>
      )

    return (
      <Table
        variant="simple"
        size="sm"
        sx={{ fontSize: 'sm', th: { p: 0.5 }, td: { p: 0.5 } }}
      >
        <Thead>
          <Tr>
            <Th>{LABEL.RULES_TABLE.index}</Th>
            <Th>
              <Flex>
                <FilterMultiSelectPopover
                  label="Organizație"
                  options={organisations.map(o => ({ value: o.cif, label: o.name}))}
                  selectedValues={companyFilter}
                  onChange={(values) => {
                    setCompanyFilter(values)
                    setPagination(prev => ({ ...prev, pageNumber: 0 }));
                  }}
                />
              </Flex>
            </Th>
            <Th cursor="pointer">
                <Flex align="center">
                    {LABEL.ARTICLE_RULES_TABLE.expenseGroup}
                    {/* <FilterPopoverWithCheck
                      label={LABEL.FILTER.label}
                      initialSelected={expenseGroups}
                      onSubmit={setExpenseGroups}
                      fetchItems={getExpenseGroups}
                      pageSize={10}
                      content={true}
                      isScrollable={true}
                      displayField='details'
                      noContent={true}
                      manualSearch={true}
                    /> */}
                    {/* posibil sa fie nevoie mai tarziu */}
                </Flex>
              </Th>
            <Th>{LABEL.ARTICLE_RULES_TABLE.articleNumber}</Th>
            <Th>{LABEL.ARTICLE_RULES_TABLE.actions}</Th>
          </Tr>
        </Thead>
        <Tbody>
          {data.content.length === 0 ? (
            <Tr>
              <Td colSpan={5} textAlign="center">
                {LABEL.EMPTY_FILTER.label}
              </Td>
            </Tr>
          ) : (
            data.content.map((group, idx) => {
              const [companyCifPart, expenseCode] =
                group.worldClassExpenseGroup.split('_')
              const company = companies.find((c) => c.cif === companyCifPart)
              const expense = allExpenseGroups.find(
                (group) => group.name === expenseCode || group.id === expenseCode
              )

              return (
                <Tr key={group.id}>
                  <Td
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.200"
                  >
                    {pagination.pageNumber * pagination.size + idx + 1}
                  </Td>
                  <Td>{company?.name || companyCifPart || '—'}</Td>
                  <Td>
                    {`${expenseCode} - ${expense?.details}` || expenseCode}
                  </Td>
                  <Td>{group.articles?.length || 0}</Td>
                  <Td>
                    <Flex gap="2">
                      <Button
                        size="sm"
                        colorScheme="blue"
                        variant="outline"
                        _hover={{ bg: 'blue.500', color: 'white' }}
                        onClick={() => openList(group)}
                      >
                        <FaList />
                      </Button>

                      <Button
                        size="sm"
                        colorScheme="green"
                        variant="outline"
                        _hover={{ bg: 'green.500', color: 'white' }}
                        onClick={() => openEdit(group)}
                      >
                        <FaEdit />
                      </Button>

                      <Button
                        colorScheme="red"
                        size="sm"
                        variant="outline"
                        _hover={{ bg: 'red.500', color: 'white' }}
                        onClick={() => handleDelete(group.id)}
                      >
                        <FaTrash />
                      </Button>
                    </Flex>
                  </Td>
                </Tr>
              )
            })
          )}
        </Tbody>
      </Table>
    )
  }

  return (
    <>
      <Flex align="center" justifyContent="space-between" width="100%" mb="5">
        <InputGroup>
          <InputLeftElement color="gray.500" pointerEvents="none">
            <FaSearch />
          </InputLeftElement>
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={LABEL.SEARCH.label}
          />
        </InputGroup>
        <Button
          ml="5"
          colorScheme="blue"
          onClick={openCreate}
          leftIcon={<FaPlus />}
        >
          {LABEL.RULES_TABLE.addRuleButton}
        </Button>
      </Flex>

      {renderTable()}

      <Flex justify="space-between" mt="4" align="center">
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() =>
            setPagination((page) => ({
              ...page,
              pageNumber: Math.max(page.pageNumber - 1, 0)
            }))
          }
          isDisabled={pagination.pageNumber === 0}
        >
          {LABEL.PREVIOUS_PAGE.label}
        </Button>
        <Text>
          Pagina {pagination.pageNumber + 1} / {totalPages}
        </Text>
        <Button
          size="sm"
          colorScheme="blue"
          onClick={() =>
            setPagination((p) => ({
              ...p,
              pageNumber: Math.min(p.pageNumber + 1, totalPages - 1)
            }))
          }
          isDisabled={pagination.pageNumber + 1 >= totalPages}
        >
          {LABEL.NEXT_PAGE.label}
        </Button>
      </Flex>

      {renderUpsertModal()}

      <ListModal
        isOpen={isListOpen}
        onClose={onListClose}
        selectedGroupName={selectedGroupName}
        selectedArticles={selectedArticles}
      />
    </>
  )
}

export default ArticlesRulesTable
